/**
 * Sheet Component
 * Drawer/slide-out panel for mobile navigation and side panels
 */

import * as React from 'react';
import { cn } from '../../lib/utils';
import X from 'lucide-react/dist/esm/icons/x';

type SheetSide = 'left' | 'right' | 'top' | 'bottom';

type SheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side?: SheetSide;
  children: React.ReactNode;
};

type SheetContentProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
};

type SheetHeaderProps = React.HTMLAttributes<HTMLDivElement>;
type SheetTitleProps = React.HTMLAttributes<HTMLHeadingElement>;
type SheetDescriptionProps = React.HTMLAttributes<HTMLParagraphElement>;

const slideVariants: Record<SheetSide, { open: string; closed: string }> = {
  left: {
    open: 'translate-x-0',
    closed: '-translate-x-full',
  },
  right: {
    open: 'translate-x-0',
    closed: 'translate-x-full',
  },
  top: {
    open: 'translate-y-0',
    closed: '-translate-y-full',
  },
  bottom: {
    open: 'translate-y-0',
    closed: 'translate-y-full',
  },
};

const sizeVariants: Record<SheetSide, string> = {
  left: 'inset-y-0 left-0 h-full w-3/4 max-w-sm',
  right: 'inset-y-0 right-0 h-full w-3/4 max-w-sm',
  top: 'inset-x-0 top-0 w-full h-auto max-h-[80vh]',
  bottom: 'inset-x-0 bottom-0 w-full h-auto max-h-[80vh]',
};

const SheetContext = React.createContext<{ side: SheetSide }>({ side: 'left' });

const Sheet = ({ open, onOpenChange, side = 'left', children }: SheetProps) => {
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open, onOpenChange]);

  return (
    <SheetContext.Provider value={{ side }}>
      <div
        className={cn(
          'fixed inset-0 z-50',
          open ? 'pointer-events-auto' : 'pointer-events-none',
        )}
      >
        {/* Backdrop — petrolio, not black (manual §0). See dialog.tsx. */}
        <div
          className={cn(
            'fixed inset-0 bg-surface-inverse/50 backdrop-blur-sm transition-opacity duration-300',
            open ? 'opacity-100' : 'opacity-0',
          )}
          onClick={() => onOpenChange(false)}
          aria-hidden="true"
        />
        {/* Content */}
        <div
          className={cn(
            'fixed bg-sidebar border-sidebar-border shadow-lg transition-transform duration-300 ease-in-out',
            sizeVariants[side],
            side === 'left' || side === 'right' ? 'border-r' : 'border-b',
            open ? slideVariants[side].open : slideVariants[side].closed,
          )}
        >
          {children}
        </div>
      </div>
    </SheetContext.Provider>
  );
};

const SheetContent = React.forwardRef<HTMLDivElement, SheetContentProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('relative h-full flex flex-col overflow-auto', className)}
      {...props}
    >
      {children}
    </div>
  ),
);
SheetContent.displayName = 'SheetContent';

const SheetHeader = ({ className, ...props }: SheetHeaderProps) => (
  <div
    className={cn(
      'flex items-center justify-between px-4 py-4 border-b border-sidebar-border',
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetTitle = React.forwardRef<HTMLHeadingElement, SheetTitleProps>(
  ({ className, ...props }, ref) => (
    <h2
      ref={ref}
      className={cn('text-lg font-semibold text-sidebar-foreground', className)}
      {...props}
    />
  ),
);
SheetTitle.displayName = 'SheetTitle';

const SheetDescription = React.forwardRef<
  HTMLParagraphElement,
  SheetDescriptionProps
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = 'SheetDescription';

type SheetCloseProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

const SheetClose = React.forwardRef<HTMLButtonElement, SheetCloseProps>(
  ({ className, onClick, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        'rounded-control text-sidebar-foreground opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        className,
      )}
      onClick={onClick}
      {...props}
    >
      <X className="h-5 w-5" />
      <span className="sr-only">Chiudi</span>
    </button>
  ),
);
SheetClose.displayName = 'SheetClose';

export {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
};
